import { useEffect, useState } from 'react'

import { Button } from '@ui/components/shadcn/ui/button'
import { Card } from '@ui/components/shadcn/ui/card'
import { notifyClient } from '@ui/lib/airbrake/client'
import {
  ACCEPT_ALL,
  CONSENT_COOKIE_NAME,
  decideConsent,
  hasGpcSignal,
  parseConsentRecord,
  REJECT_ALL,
  type ConsentDecisionAction,
  type ConsentState,
} from '@ui/lib/consent'
import { readCookie } from '@ui/lib/cookies'
import { marketingUrl } from '@ui/lib/urls'

import { CookiePreferencesDialog } from './CookiePreferencesDialog'

// A notice, not a wall: no overlay, focus trap, or close button. Bottom-center
// on wide screens, clear of the homepage's QR card.
export function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  // No decision and no signal shows it; a signal is already an answer.
  useEffect(() => {
    const raw = readCookie(CONSENT_COOKIE_NAME)
    const record = parseConsentRecord(raw)
    if (raw && !record) {
      // Self-heals (the banner returns); a spike after a deploy means the writer broke.
      notifyClient({
        error: new Error('Malformed consent cookie'),
        context: { component: 'CookieBanner', severity: 'warning' },
      })
    }
    setVisible(!record && !hasGpcSignal())
  }, [])

  const decide = async (state: ConsentState, action: ConsentDecisionAction) => {
    await decideConsent({ state, action })
    setVisible(false)
  }

  if (!visible) return null
  return (
    <>
      <Card
        role="region"
        aria-label="Cookie notice"
        className="animate-in fade-in slide-in-from-bottom-4 fixed inset-x-4 bottom-4 z-50 p-4 duration-300 sm:inset-x-0 sm:mx-auto sm:w-full sm:max-w-md"
      >
        <p className="text-sm">
          We use cookies to understand how our site is used and to measure our
          advertising. You can allow or reject advertising cookies, or manage
          your preferences. See our{' '}
          <a href={marketingUrl('/privacy-policy')} className="underline">
            Privacy Policy
          </a>
          .
        </p>
        <div className="mt-2 flex flex-col items-center sm:flex-row sm:justify-center sm:gap-2">
          <Button variant="link" onClick={() => setPreferencesOpen(true)}>
            Manage Preferences
          </Button>
          <Button
            variant="link"
            onClick={() => decide(REJECT_ALL, 'bannerRejectAll')}
          >
            Reject All
          </Button>
          <Button
            variant="link"
            onClick={() => decide(ACCEPT_ALL, 'bannerAcceptAll')}
          >
            Accept All
          </Button>
        </div>
      </Card>
      <CookiePreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        onDecided={() => setVisible(false)}
      />
    </>
  )
}
